import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { useMe } from '../auth/useMe';
import { useIsPhone } from '../events/EventsPage';
import { ConflictNotice } from '../ui/ConflictNotice';
import { RowMenu } from '../ui/RowMenu';
import { BuyerEditor, BuyerView } from './BuyerEditor';
import { CorrectionDialog, CorrectionLines } from './CorrectionEditor';
import { InvoiceLines } from './InvoiceLines';
import { InvoicePdfPreview } from './InvoicePdfPreview';
import { InvoiceTotals } from './InvoiceTotals';
import { KsefBadge, waitingForKsef } from './KsefBadge';
import { downloadInvoicePdf, EMAIL_STATUS_LABELS, upoUrl } from './pdfLink';
import { SendInvoiceDialog } from './SendInvoiceDialog';
import { dayText, draftPdfAllowed, GROUPING_LABELS, ibanText, INVOICE_SETTINGS_RESOURCE, INVOICES_KEY, INVOICES_RESOURCE, KIND_TITLES, MISSING_LABELS, nipText, PAYMENT_LABELS, type Grouping, type Invoice, type InvoiceSettings } from './types';
import { planShareNote } from './planShare';
import './invoices.css';
import { leaveWithoutAsking, useLeavePrompt, useRowGuard } from '../ui/unsaved';
import { differenceText, IssueConfirmDialog, leaveQuestion, LeaveQuestionDialog } from './InvoiceQuestions';

/** The Polish message of a refusal; "Uzupełnij dane firmy" links to the settings. */
function Refusal({ error }: { error: unknown }) {
  if (!error) return null;
  if (error instanceof ApiError && error.status === 409) return null;
  const message = error instanceof ApiError ? (fieldIssues(error)[0]?.message ?? error.message) : error instanceof Error ? error.message : 'Nie udało się zapisać';
  const seller = error instanceof ApiError && error.code === 'seller_incomplete';
  const missing = seller ? ((error.details as { missing?: string[] } | undefined)?.missing ?? []) : [];
  return (
    <p className="alert" role="alert">
      {seller ? `Uzupełnij dane firmy: ${missing.map((m) => MISSING_LABELS[m] ?? m).join(', ')}. ` : message}
      {seller && <Link to="/finanse/ustawienia">Przejdź do ustawień</Link>}
    </p>
  );
}

/** Seller's block of the document (from "Dane do faktur"), when the user may read the settings. */
function SellerView({ me }: { me: Me }) {
  const settings = useQuery({ queryKey: [...INVOICES_KEY, 'settings'], queryFn: () => api<InvoiceSettings>('/api/invoices/settings'), enabled: can(me, INVOICE_SETTINGS_RESOURCE, 'view') });
  const s = settings.data;
  if (!s) return null;
  return (
    <address className="inv-party-body">
      <strong>{s.sellerName}</strong>
      {s.sellerAddressL1 && <span>{s.sellerAddressL1}</span>}
      {s.sellerAddressL2 && <span>{s.sellerAddressL2}</span>}
      {s.sellerNip ? <span>NIP: {nipText(s.sellerNip)}</span> : <span className="todo-mark">brak NIP</span>}
      {s.sellerIban && (
        <span className="muted">
          Rachunek: {ibanText(s.sellerIban)}
          {s.sellerBankName && ` (${s.sellerBankName})`}
        </span>
      )}
    </address>
  );
}

/**
 * Which rule of invoice lines made the lines ("Według reguł"), or that none matched and the lines are by sections;
 * "Zarządzaj regułami" leads to the settings (rule 9).
 */
function RuleNote({ invoice, me }: { invoice: Invoice; me: Me }) {
  const manage = can(me, INVOICE_SETTINGS_RESOURCE, 'view') && (
    <Link className="manage-link" to="/finanse/ustawienia#reguly-pozycji">
      ⚙ Zarządzaj regułami
    </Link>
  );
  return (
    <p className="inv-rule-note" role="status" data-feature-id="invoices.line_rules">
      {invoice.lineRuleName ? (
        <>
          Reguła: <strong>{invoice.lineRuleName}</strong>{' '}
        </>
      ) : (
        'Żadna reguła nie pasuje do tego kosztorysu – pozycje są według sekcji. '
      )}
      {manage}
    </p>
  );
}

/**
 * "Finanse → Faktury → <faktura>": the invoice as a document. A draft is corrected in place (buyer, dates, payment,
 * grouping, lines, notes) and issued with "Wystaw"; a rejected one is corrected and sent again; the card asks again
 * every 5 s while KSeF has not answered.
 */
/** The invoice's notes: saved when the field is left; typed and not saved yet, leaving waits for it (owner 2026-10-23 21:14). */
function NotesCell({ notes, onSave }: { notes: string; onSave: (notes: string) => Promise<unknown> }) {
  const [text, setText] = useState(notes);
  const row = useRowGuard(text.trim() !== notes, async () => {
    if (text.trim() !== notes) await onSave(text);
    return true;
  });
  return <textarea ref={row.ref} rows={2} value={text} onChange={(e) => setText(e.target.value)} onBlur={() => void row.save()} />;
}

/** `GET /api/box/invoices/:id` (owner 2026-11-03): whether Box is set, when the PDF was saved there, or that saving it was given up (and why, when the owner can act on it). */
type BoxStatus = { configured: boolean; savedAt: string | null; failed: boolean; failedReason: string | null };
const BOX_POLLS = 40;
const WHEN = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export function InvoiceCard() {
  const { id = '' } = useParams();
  const { me } = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const key = [...INVOICES_KEY, 'card', id];
  // asked again while KSeF has not answered and while the e-mail to the client is still queued
  const card = useQuery({ queryKey: key, queryFn: () => api<Invoice>(`/api/invoices/${id}`), refetchInterval: (q) => (q.state.data && (waitingForKsef(q.state.data) || q.state.data.emailStatus === 'queued') ? 5000 : false) });
  const [editingBuyer, setEditingBuyer] = useState(false);
  // `then`: where the user was going (the question before leaving: „Wyślij…” leaves after the send)
  const [sending, setSending] = useState<false | { then?: () => void }>(false);
  // owner 2026-11-01: „Wystaw” asks „Wysłać fakturę do KSeF?” first
  // review 1: every way to KSeF is confirmed — „Wystaw”, „Nadaj nowy numer i wyślij”, „Popraw i wyślij ponownie” (with its body)
  const [confirming, setConfirming] = useState<null | { mode: 'issue' } | { mode: 'renumber' } | { mode: 'resubmit'; body: Record<string, unknown>; buyerName?: string }>(null);
  const setConfirmingIssue = (on: boolean) => setConfirming(on ? { mode: 'issue' } : null);
  const [leaving, setLeaving] = useState<{ go: () => void } | null>(null);
  const [correcting, setCorrecting] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);
  // owner 2026-10-20 23:51: "Podgląd PDF" of a draft next to the card (full screen on a phone)
  const [previewing, setPreviewing] = useState(false);
  const phone = useIsPhone();
  const [error, setError] = useState<unknown>(null);
  const saved = (inv: Invoice) => {
    setError(null);
    queryClient.setQueryData(key, inv);
    void queryClient.invalidateQueries({ queryKey: [...INVOICES_KEY, 'list'] });
  };
  const inv = card.data;
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<Invoice>(`/api/invoices/${id}`, { method: 'PATCH', body: { ...body, version: inv!.version } }),
    onSuccess: (v) => {
      setEditingBuyer(false);
      saved(v);
    },
    onError: setError,
  });
  const issue = useMutation({
    mutationFn: () => api<Invoice>(`/api/invoices/${id}/issue`, { method: 'POST', body: { version: inv!.version } }),
    onSuccess: saved,
    onError: setError,
    onSettled: () => setConfirmingIssue(false),
  });
  const dismissDifference = useMutation({ mutationFn: () => api<Invoice>(`/api/invoices/${id}/difference/dismiss`, { method: 'POST', body: { version: inv!.version } }), onSuccess: saved, onError: setError });
  const cancelAutoSend = useMutation({ mutationFn: () => api<Invoice>(`/api/invoices/${id}/auto-send?version=${inv!.version}`, { method: 'DELETE' }), onSuccess: saved, onError: setError });
  // owner 2026-11-03: the PDF in Box — asked only for an invoice with a KSeF number, by whoever sees its PDF (every amount)
  const boxKey = ['box', 'invoice', id];
  const inBox = !!inv && inv.state === 'wystawiona' && inv.ksefState !== 'odrzucona' && !!inv.ksefNumber && inv.pdfAllowed === true;
  // the automatic upload runs in the background after the KSeF number: asked again for a while (about 2 minutes) until saved or given up
  const box = useQuery({
    queryKey: boxKey,
    queryFn: () => api<BoxStatus>(`/api/box/invoices/${id}`),
    enabled: inBox,
    retry: false,
    refetchInterval: (q) => (q.state.data?.configured && !q.state.data.savedAt && !q.state.data.failed && q.state.dataUpdateCount < BOX_POLLS ? 3000 : false),
  });
  const saveInBox = useMutation({
    mutationFn: () => api<BoxStatus>(`/api/box/invoices/${id}/save`, { method: 'POST' }),
    onSuccess: (v) => {
      setError(null);
      queryClient.setQueryData(boxKey, v);
    },
    onError: setError,
  });
  // owner 2026-11-01: leaving the card (another screen, the browser's "Wstecz") asks once — about what the user may do
  const question = me && inv ? leaveQuestion(inv, { mayIssue: can(me, INVOICES_RESOURCE, 'issue'), maySend: can(me, INVOICES_RESOURCE, 'send') && inv.grossGrosze !== undefined && inv.lines.every((l) => l.netGrosze !== undefined) }) : null;
  useLeavePrompt(question !== null, { leaves: (to) => new URL(to, 'http://app.invalid').pathname.replace(/\/+$/, '') !== `/finanse/faktury/${id}`, ask: (go) => setLeaving({ go }) });
  // nothing to ask any more while the question is open (e.g. KSeF gave the number and the e-mail went): the leaving goes on
  useEffect(() => {
    if (!leaving || question) return;
    setLeaving(null);
    leaveWithoutAsking(leaving.go);
  }, [leaving, question]);
  const leave = () => {
    const go = leaving?.go;
    setLeaving(null);
    if (go) leaveWithoutAsking(go);
  };
  const resubmit = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<Invoice>(`/api/invoices/${id}/resubmit`, { method: 'POST', body: { ...body, version: inv!.version } }),
    onSuccess: (v) => {
      setResubmitting(false);
      saved(v);
    },
    onError: setError,
    onSettled: () => setConfirming(null),
  });
  const renumber = useMutation({ mutationFn: () => api<Invoice>(`/api/invoices/${id}/renumber`, { method: 'POST', body: { version: inv!.version } }), onSuccess: saved, onError: setError, onSettled: () => setConfirming(null) });
  const cancel = useMutation({ mutationFn: () => api<Invoice>(`/api/invoices/${id}/cancel`, { method: 'POST', body: { version: inv!.version } }), onSuccess: saved, onError: setError });
  const rebuild = useMutation({ mutationFn: () => api<Invoice>(`/api/invoices/${id}/rebuild`, { method: 'POST', body: { version: inv!.version } }), onSuccess: saved, onError: setError });
  const remove = useMutation({
    mutationFn: () => api<void>(`/api/invoices/${id}?version=${inv!.version}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
      navigate('/finanse/faktury');
    },
    onError: setError,
  });
  if (!me) return null;
  if (card.error) {
    return (
      <section className="page wide">
        <p className="alert" role="alert">
          {card.error instanceof ApiError ? card.error.message : 'Nie udało się wczytać faktury'}
        </p>
      </section>
    );
  }
  if (!inv) return <p className="muted">Wczytywanie…</p>;

  const draft = inv.state === 'robocza';
  const mayIssue = can(me, INVOICES_RESOURCE, 'issue');
  // rejected by KSeF: never accepted, so not issued in law — its lines, dates and buyer are still corrected here
  const rejected = inv.state === 'wystawiona' && inv.ksefState === 'odrzucona';
  // F35 review I1: rejected after the client got its PDF — corrected and sent to KSeF again (never cancelled)
  const deliveredRejected = rejected && inv.clientHasPdf;
  const editable = (draft || rejected) && can(me, INVOICES_RESOURCE, 'edit');
  // the PDF shows every amount: offered to whoever sees them (the server checks the account number too)
  const seesAmounts = inv.grossGrosze !== undefined && inv.lines.every((l) => l.netGrosze !== undefined);
  const issuedValid = inv.state === 'wystawiona' && !rejected;
  const maySend = can(me, INVOICES_RESOURCE, 'send') && seesAmounts;
  // the PDF can be made now (KSeF number, or issued offline) even if making it failed earlier: the server makes it on demand
  const pdfReady = issuedValid && (!!inv.pdfFileId || !!inv.ksefNumber || inv.ksefState === 'offline');
  const downloadPdf = () => {
    downloadInvoicePdf(inv.id);
    // a PDF made on demand: read the card again for its file
    if (!inv.pdfFileId) setTimeout(() => void card.refetch(), 1500);
  };
  const duplicateNumber = rejected && inv.ksefErrorCode === '440';
  const conflict = error instanceof ApiError && error.status === 409;
  // F40: an advance invoice was issued after this draft was made — rebuilt, it becomes the final one
  const advancesChanged = draft && error instanceof ApiError && error.code === 'advances_changed';
  const correction = inv.kind === 'KOR';
  // F40: an invoice in KSeF is changed (or cancelled, to zero) only by a correction — of the server's world (test / real)
  const mayCorrect = inv.correctable && can(me, INVOICES_RESOURCE, 'correct');
  const menu = [
    ...(mayCorrect ? [{ label: 'Korekta…', onSelect: () => setCorrecting(true) }] : []),
    ...(draft && can(me, INVOICES_RESOURCE, 'delete') ? [{ label: 'Usuń fakturę roboczą', danger: true, onSelect: () => remove.mutate() }] : []),
    ...(draftPdfAllowed(inv) ? [{ label: 'Pobierz PDF (projekt)', onSelect: () => downloadInvoicePdf(inv.id) }] : []),
    ...(rejected && !inv.clientHasPdf && mayIssue ? [{ label: 'Anuluj fakturę (nie trafiła do KSeF)', danger: true, onSelect: () => cancel.mutate() }] : []),
    ...(inBox && box.data?.configured && can(me, INVOICES_RESOURCE, 'send') ? [{ label: saveInBox.isPending ? 'Zapisywanie w Box…' : 'Zapisz w Box', disabled: saveInBox.isPending, onSelect: () => saveInBox.mutate() }] : []),
  ];
  const boxText = !inBox || !box.data?.configured ? null : box.data.savedAt ? `Zapisano w Box ${WHEN.format(new Date(box.data.savedAt))}` : box.data.failed ? `Nie udało się zapisać w Box – ${box.data.failedReason ?? 'użyj „Zapisz w Box”'}` : null;
  const emailText = inv.emailStatus ? `E-mail do ${inv.sentTo ?? 'klienta'}: ${EMAIL_STATUS_LABELS[inv.emailStatus]}` : null;

  return (
    <section className="page wide inv-card" aria-label="Faktura" data-feature-id="invoices.card">
      <p className="inv-back">
        <Link to="/finanse/faktury">← Faktury</Link>
      </p>
      <header className="inv-head">
        <div>
          <h2>{correction && draft ? 'Korekta robocza' : draft ? `${KIND_TITLES[inv.kind]} robocza` : `${KIND_TITLES[inv.kind]} ${inv.number}`}</h2>
          {inv.previousNumbers.length > 0 && <p className="muted">Wcześniejszy numer (odrzucony przez KSeF): {inv.previousNumbers.join(', ')}</p>}
          {inv.state === 'zewnetrzna' ? (
            <p className="muted">
              Wystawiona poza programem, pobrana z KSeF – tylko do odczytu
              {inv.test && ' · faktura testowa (bez mocy prawnej)'}
            </p>
          ) : correction ? (
            <p className="muted">
              <Link to={`/wydarzenia?wydarzenie=${inv.eventId}&zakladka=kosztorys`}>{inv.eventName || 'wydarzenie'}</Link>
              {inv.test && ' · faktura testowa (bez mocy prawnej)'}
            </p>
          ) : (
            <p className="muted">
              Na podstawie: kosztorys v{inv.quoteVersionNumber} ·{' '}
              <Link to={`/wydarzenia?wydarzenie=${inv.eventId}&zakladka=kosztorys`}>{inv.eventName || 'wydarzenie'}</Link>
              {inv.test && ' · faktura testowa (bez mocy prawnej)'}
            </p>
          )}
        </div>
        <div className="inv-head-actions">
          <KsefBadge invoice={inv} />
          {draftPdfAllowed(inv) && (
            <button className="btn" type="button" aria-pressed={previewing} onClick={() => setPreviewing((v) => !v)} data-feature-id="invoices.pdf_preview">
              {previewing ? 'Zamknij podgląd' : 'Podgląd PDF'}
            </button>
          )}
          {draft && mayIssue && !inv.outdated && (
            <button className="btn primary" type="button" disabled={issue.isPending} onClick={() => setConfirmingIssue(true)} data-feature-id="invoices.issue">
              {issue.isPending ? 'Wystawianie…' : 'Wystaw'}
            </button>
          )}
          {issuedValid && maySend && (inv.canSend || !inv.pdfFileId) && (
            <button className="btn primary" type="button" disabled={!(inv.canSend || (pdfReady && !inv.pdfFileId))} onClick={() => setSending({})} data-feature-id="invoices.send" title={pdfReady ? undefined : 'PDF powstaje po nadaniu numeru KSeF'}>
              {!pdfReady ? 'Czeka na KSeF' : inv.sentAt && !inv.clientPdfOutdated ? 'Wyślij ponownie' : 'Wyślij do klienta'}
            </button>
          )}
          {pdfReady && seesAmounts && (
            <button className="btn" type="button" onClick={downloadPdf} data-feature-id="invoices.pdf">
              Pobierz PDF
            </button>
          )}
          {duplicateNumber && mayIssue && (
            <button className="btn primary" type="button" disabled={renumber.isPending} onClick={() => setConfirming({ mode: 'renumber' })}>
              Nadaj nowy numer i wyślij
            </button>
          )}
          {rejected && !duplicateNumber && mayIssue && !resubmitting && (
            <button className="btn primary" type="button" onClick={() => setResubmitting(true)}>
              Popraw i wyślij ponownie
            </button>
          )}
          {menu.length > 0 && <RowMenu label="Więcej działań faktury" actions={menu} />}
        </div>
      </header>
      {inv.ksefNumber && (
        <p className="inv-ksef">
          Numer KSeF: {inv.ksefNumber}
          {inv.upoAvailable && (
            <>
              {' · '}
              <a href={upoUrl(inv.id)} download="">
                Pobierz UPO
              </a>
            </>
          )}
        </p>
      )}
      {inv.attention.map((a) => (
        <p key={a.code + a.message} className="alert" role="alert">
          {a.message}
        </p>
      ))}
      {emailText && (
        <p className="inv-email-status" data-status={inv.emailStatus ?? undefined} role="status" data-feature-id="invoices.send">
          {emailText}
        </p>
      )}
      {boxText && (
        <p className="inv-box-status muted" role="status" data-feature-id="box.invoices">
          {boxText}
        </p>
      )}
      {deliveredRejected && (
        <p className="alert" role="alert">
          Klient dostał już PDF tej faktury, zanim KSeF ją odrzucił. Popraw ją i wyślij ponownie do KSeF – po przyjęciu wyślij klientowi poprawiony PDF. Tej faktury nie można anulować.
        </p>
      )}
      {issuedValid && inv.clientPdfOutdated && inv.pdfFileId && (
        <p className="alert" role="alert">
          Klient ma wcześniejszy PDF tej faktury (sprzed poprawki) – wyślij mu poprawiony przyciskiem „Wyślij do klienta”.
        </p>
      )}
      {inv.autoSend && (
        <p className="inv-auto-send" role="status" data-feature-id="invoices.send">
          Wysyłka do klienta zaplanowana po nadaniu numeru KSeF (do {inv.autoSend.to}
          {inv.autoSend.cc && `, DW ${inv.autoSend.cc}`}).
          {can(me, INVOICES_RESOURCE, 'send') && (
            <button className="btn small" type="button" disabled={cancelAutoSend.isPending} onClick={() => cancelAutoSend.mutate()}>
              Anuluj wysyłkę
            </button>
          )}
        </p>
      )}
      {inv.autoSendFailed && (
        <p className="alert" role="alert">
          Nie udało się wysłać automatycznie – wyślij ręcznie przyciskiem „Wyślij do klienta” (PDF faktury nie powstał na czas).
        </p>
      )}
      {inv.autoSendCancelled && (
        <p className="alert" role="alert">
          KSeF odrzucił fakturę, więc zaplanowana wysyłka do klienta została anulowana. Po poprawieniu i przyjęciu faktury wyślij ją przyciskiem „Wyślij do klienta”.
        </p>
      )}
      {correcting && <CorrectionDialog invoice={inv} onClose={() => setCorrecting(false)} />}
      {sending && (
        <SendInvoiceDialog
          invoice={inv}
          onClose={() => setSending(false)}
          onReload={() => {
            setSending(false);
            void card.refetch();
          }}
          onDone={(v) => {
            const then = sending.then;
            setSending(false);
            saved(v);
            if (then) leaveWithoutAsking(then);
          }}
        />
      )}
      {confirming && (
        <IssueConfirmDialog
          invoice={inv}
          mode={confirming.mode}
          {...(confirming.mode === 'resubmit' && confirming.buyerName !== undefined ? { buyerName: confirming.buyerName } : {})}
          busy={issue.isPending || renumber.isPending || resubmit.isPending}
          onConfirm={() => (confirming.mode === 'issue' ? issue.mutate() : confirming.mode === 'renumber' ? renumber.mutate() : resubmit.mutate(confirming.body))}
          onClose={() => setConfirming(null)}
        />
      )}
      {leaving && question && (
        <LeaveQuestionDialog
          invoice={inv}
          question={question}
          onIssue={() => {
            // „Wystaw…”: through the confirmation; the card stays (KSeF answers in a moment)
            setLeaving(null);
            setConfirmingIssue(true);
          }}
          onSend={() => {
            const go = leaving.go;
            setLeaving(null);
            setSending({ then: go });
          }}
          onPlanned={(v) => {
            saved(v);
            leave();
          }}
          onLeave={leave}
          onStay={() => setLeaving(null)}
        />
      )}
      {rejected && inv.ksefError && (
        <p className="alert" role="alert">
          KSeF odrzucił fakturę: {inv.ksefError}
          {duplicateNumber ? ' Ten numer jest już w KSeF – faktura dostanie kolejny numer z dzisiejszą datą.' : ` Popraw ją (nabywcę, pozycje, daty) i wyślij ponownie – numer zostaje ten sam${inv.clientHasPdf ? '.' : ', albo anuluj ją w menu „…”.'}`}
        </p>
      )}
      {draft && inv.outdated && (
        <div className="alert" role="alert">
          Kosztorys v{inv.quoteVersionNumber} nie jest już zaakceptowany – tej faktury nie można wystawić.{' '}
          {can(me, INVOICES_RESOURCE, 'edit') && (
            <button className="btn small primary" type="button" disabled={rebuild.isPending} onClick={() => rebuild.mutate()}>
              Przebuduj z zaakceptowanej wersji
            </button>
          )}
        </div>
      )}
      {conflict && (
        <ConflictNotice
          onReload={() => {
            setError(null);
            void card.refetch();
          }}
        />
      )}
      <Refusal error={error} />
      {advancesChanged && can(me, INVOICES_RESOURCE, 'edit') && (
        <p>
          <button className="btn small primary" type="button" disabled={rebuild.isPending} onClick={() => rebuild.mutate()}>
            Przebuduj z kosztorysu
          </button>
        </p>
      )}
      {draft && inv.planShare && (
        <p className="muted" role="note">
          {planShareNote(inv.planShare)}
        </p>
      )}
      {/* owner 2026-11-01: „dodaj pytanie, czy wczytać informacje z kosztorysu”; „Zostaw fakturę” remembered for this difference */}
      {draft && inv.differenceGrosze !== undefined && inv.differenceGrosze !== 0 && !inv.differenceDismissed && (
        <p className="inv-diff" role="status" data-feature-id="invoices.card">
          {differenceText(inv)}
          {can(me, INVOICES_RESOURCE, 'edit') && !inv.outdated && (
            <span className="inv-diff-actions">
              <button className="btn small primary" type="button" disabled={rebuild.isPending || dismissDifference.isPending} onClick={() => rebuild.mutate()}>
                Wczytaj z kosztorysu
              </button>
              <button className="btn small" type="button" disabled={rebuild.isPending || dismissDifference.isPending} onClick={() => dismissDifference.mutate()}>
                Zostaw fakturę
              </button>
            </span>
          )}
        </p>
      )}
      {resubmitting && (
        <BuyerEditor
          buyer={inv.buyer}
          label="Popraw i wyślij ponownie"
          submitLabel="Wyślij ponownie"
          busy={resubmit.isPending}
          error={error}
          onSubmit={(b) => setConfirming({ mode: 'resubmit', body: Object.keys(b).length > 0 ? { buyer: b } : {}, ...(typeof (b as { name?: unknown }).name === 'string' ? { buyerName: (b as { name: string }).name } : {}) })}
          guard={false}
          onCancel={() => setResubmitting(false)}
        />
      )}

      <div className={previewing && draftPdfAllowed(inv) && !phone ? 'inv-with-preview' : undefined}>
        <article className="inv-doc">
          <div className="inv-parties">
            <div className="inv-party">
              <h3>Sprzedawca</h3>
              <SellerView me={me} />
            </div>
            <div className="inv-party">
              <h3>
                Nabywca
                {editable && !editingBuyer && !correction && (
                  <button className="btn text small" type="button" onClick={() => setEditingBuyer(true)}>
                    Zmień
                  </button>
                )}
              </h3>
              {editingBuyer ? (
                <BuyerEditor buyer={inv.buyer} label="Nabywca" submitLabel="Zapisz nabywcę" busy={patch.isPending} error={error} onSubmit={(b) => (Object.keys(b).length > 0 ? patch.mutateAsync({ buyer: b }) : setEditingBuyer(false))} onCancel={() => setEditingBuyer(false)} />
              ) : (
                <BuyerView buyer={inv.buyer} />
              )}
            </div>
          </div>

          <dl className="inv-dates">
            <div>
              <dt>Data wystawienia</dt>
              <dd>{draft ? 'przy wystawieniu' : dayText(inv.issueDate)}</dd>
            </div>
            <div>
              <dt>Data sprzedaży</dt>
              <dd>{editable ? <input type="date" aria-label="Data sprzedaży" value={inv.saleDate} onChange={(e) => e.target.value && patch.mutate({ saleDate: e.target.value })} /> : dayText(inv.saleDate)}</dd>
            </div>
            <div>
              <dt>Termin płatności</dt>
              <dd>{editable ? <input type="date" aria-label="Termin płatności" value={inv.dueDate} onChange={(e) => e.target.value && patch.mutate({ dueDate: e.target.value })} /> : dayText(inv.dueDate)}</dd>
            </div>
            <div>
              <dt>Sposób płatności</dt>
              <dd>
                {editable ? (
                  <select aria-label="Sposób płatności" value={inv.paymentMethod} onChange={(e) => patch.mutate({ paymentMethod: e.target.value })}>
                    {(Object.keys(PAYMENT_LABELS) as Array<Invoice['paymentMethod']>).map((m) => (
                      <option key={m} value={m}>
                        {PAYMENT_LABELS[m]}
                      </option>
                    ))}
                  </select>
                ) : (
                  PAYMENT_LABELS[inv.paymentMethod]
                )}
              </dd>
            </div>
          </dl>

          {editable && draft && !correction && (
            <div className="inv-modes">
              <div className="segment inv-grouping" role="group" aria-label="Pozycje faktury">
                {(Object.keys(GROUPING_LABELS) as Grouping[]).map((g) => (
                  <button key={g} type="button" aria-pressed={inv.grouping === g} disabled={patch.isPending || inv.outdated} onClick={() => inv.grouping !== g && patch.mutate({ grouping: g })}>
                    {GROUPING_LABELS[g]}
                  </button>
                ))}
              </div>
              {/* review M4: the lines of an outdated draft come from a version no longer accepted (F34 C1) */}
              {inv.outdated ? (
                <p className="inv-rule-note" role="status">
                  Tryb pozycji zmienisz po przebudowaniu faktury z zaakceptowanej wersji kosztorysu (przycisk „Przebuduj z zaakceptowanej wersji” wyżej).
                </p>
              ) : (
                inv.grouping === 'reguly' && <RuleNote invoice={inv} me={me} />
              )}
            </div>
          )}
          {correction ? (
            <CorrectionLines key={inv.version} invoice={inv} editable={editable} onSaved={saved} onError={setError} />
          ) : (
            <div data-feature-id={inv.kind === 'ZAL' || inv.kind === 'ROZ' ? 'invoices.advance' : undefined}>
              {inv.kind === 'ZAL' && <h3 className="inv-kor-caption">Zamówienie (kwota faktury wynika z wpłaty)</h3>}
              <InvoiceLines key={inv.version} invoice={inv} editable={editable && inv.kind !== 'ZAL'} label={inv.kind === 'ZAL' ? 'Pozycje zamówienia' : 'Pozycje faktury'} onSaved={saved} onError={setError} />
              <InvoiceTotals invoice={inv} />
            </div>
          )}

          <label className="field inv-notes">
            <span>Uwagi</span>
            {editable ? <NotesCell key={inv.version} notes={inv.notes} onSave={(notes) => patch.mutateAsync({ notes })} /> : <span>{inv.notes || '—'}</span>}
          </label>
        </article>
        {previewing && draftPdfAllowed(inv) && <InvoicePdfPreview invoiceId={inv.id} version={inv.version} full={phone} onClose={() => setPreviewing(false)} />}
      </div>
    </section>
  );
}
